define((require) => {
    "use strict";

    const {pick}                                 = require("underscore");
    const ButtonWidgetPropertiesView             = require("ui/experience/widgets/button/ButtonWidgetPropertiesView");
    const CompositeWidgetPropertiesView          = require("ui/experience/widgets/compositeWidget/CompositeWidgetPropertiesView");
    const EmbedWidgetPropertiesView              = require("ui/experience/widgets/embedWidget/EmbedWidgetPropertiesView");
    const HtmlWidgetPropertiesView              = require("ui/experience/widgets/htmlWidget/HtmlWidgetPropertiesView");
    const ImageWidgetPropertiesView              = require("ui/experience/widgets/imageWidget/ImageWidgetPropertiesView");
    const MaterialIcons                          = require("ui/experience/widgets/button/MaterialIcons");
    const LineWidgetPropertiesView               = require("ui/experience/widgets/lineWidget/LineWidgetPropertiesView");
    const LinkWidgetPropertiesView               = require("ui/experience/widgets/linkWidget/LinkWidgetPropertiesView");
    const ProductWidgetPropertiesView            = require("ui/experience/widgets/productWidget/ProductWidgetPropertiesView");
    const SalesforceCommerceWidgetModel          = require("shared/ui/experience/widgets/salesforceCommerceWidget/model/SalesforceCommerceWidgetModel");
    const SalesforceCommerceWidgetPropertiesView = require("ui/experience/widgets/salesforceCommerceWidget/view/SalesforceCommerceWidgetPropertiesView");
    const SalesforceCommerceConnect              = require("ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceConnect");
    const SceneNavigationAction                  = require("shared/ui/experience/action/sceneNavigation/SceneNavigationAction");
    const SceneNavigatorWidgetPropertiesView     = require("ui/experience/widgets/sceneNavigatorWidget/SceneNavigatorWidgetPropertiesView");
    const ShapeWidgetPropertiesView              = require("ui/experience/widgets/shapeWidget/ShapeWidgetPropertiesView");
    const Size                                   = require("shared/util/Size");
    const SocialSharingWidgetPropertiesView      = require("ui/experience/widgets/socialSharingWidget/SocialSharingWidgetPropertiesView");
    const Storage                                = require("shared/services/Storage");
    const TextEnhancedWidgetPropertiesView       = require("ui/experience/widgets/textEnhancedWidget/TextWidgetPropertiesView");
    const TextWidgetPropertiesView               = require("ui/experience/widgets/textWidget/TextWidgetPropertiesView");
    const YoutubeWidgetPropertiesView            = require("ui/experience/widgets/youtubeWidget/YouTubeWidgetPropertiesView");
    const DynamicProductWidgetPropertiesView     = require("ui/experience/widgets/dynamicProductWidget/DynamicProductWidgetPropertiesView");
    const WidgetTypes                            = require("shared/ui/experience/widgets/WidgetTypes");
    const LoggedInUserState                      = require("shared/ui/user/LoggedInUserState");


    function suspendForEmailMode(viewModel) {
        const isEmailMode = viewModel.get("experience").get("emailModeEnabled");
        this.suspendedTooltip = "This widget cannot be used in email(s)";
        this.suspended = isEmailMode;
    }

    /** Common initializer for widget types not requiring a specific initializer. */
    const centerModel = (model) => {
        if (model.has("width") && model.has("height")) {
            model.set({
                left: model.get("left") - (model.get("width")  / 2),
                top:  model.get("top")  - (model.get("height") / 2)
            });
        }
    };

    /**
     * Mixes in Editor related WidgetType state. <p>
     *
     * Each constant can define an optional <tt>initializer(model: WidgetModel, experienceSize: Size)</tt>
     * function to be called immediately after creation in the editor when the widget icon is dropped on a Scene. <p>
     *
     * For widgets that can be disabled, the <tt>enabled(): Boolean</tt> callback can be implemented. If not specified,
     * the widget is enabled by default. <p>
     *
     * The <tt>order</tt> property determines the visual ordering of the widget icons in the side bar when editing
     * an Experience. A null <tt>order</tt> can be used for any prototypical widget to make it come last.
     *
     * @class WidgetTypes
     */
    return WidgetTypes.mixin({
        COMPOSITE: {
            order: 150,
            propertiesViewClass: CompositeWidgetPropertiesView,
            tooltip: "Create a link of any size on top of anything on the scene.",
            freeTransformConfig: {
                rotate: false,
                scaleUniform: false,
                scaleX: false,
                scaleY: false
            },
            enabled() {
                return false;
            }
        },
        DYNAMIC_PRODUCT: {
            order: 160,
            propertiesViewClass: DynamicProductWidgetPropertiesView,
            tooltip: "Creates an Enhanced Product Widget",
            freeTransformConfig: {
                rotate: false,
                scaleUniform: false,
                scaleX: false,
                scaleY: false
            },
            enabled() {
                return LoggedInUserState.get("companySettings").get("dynamicProductWidgetEnabled");
            },
            initializer: centerModel,
            suspend: suspendForEmailMode
        },
        SALESFORCE_COMMERCE: {
            order: 120, // last
            propertiesViewClass: SalesforceCommerceWidgetPropertiesView,
            tooltip: "Create links to the products in your Salesforce Commerce Cloud Account.",
            hideOnBackgroundScene: true,
            initializer(model, experienceSize) {
                // Transform the fixedSize to the equivalent percentage.
                model.set({
                    width:  (SalesforceCommerceWidgetModel.FIXED_SIZE.width * 100)  / experienceSize.width,
                    height: (SalesforceCommerceWidgetModel.FIXED_SIZE.height * 100) / experienceSize.height
                });
            },
            enabled() {
                return SalesforceCommerceConnect.isWidgetEnabled();
            },
            suspend: suspendForEmailMode
        },
        EMBED: {
            order: 40,
            propertiesViewClass: EmbedWidgetPropertiesView,
            tooltip: "Embed a URL.",
            freeTransformConfig: {
                rotate: false
            },
            initializer: centerModel,
            suspend: suspendForEmailMode
        },
        HTML: {
            order: 41,
            propertiesViewClass: HtmlWidgetPropertiesView,
            tooltip: "Insert HTML.",
            freeTransformConfig: {
                rotate: false
            },
            initializer: centerModel,
            enabled() {
                return LoggedInUserState.get("companySettings").get("htmlWidgetEnabled");
            },
            suspend: suspendForEmailMode
        },
        IMAGE: {
            order: 20,
            propertiesViewClass: ImageWidgetPropertiesView,
            tooltip: "Insert an image.",
            initializer(model, experienceSize) {
                const isLargerWidth = model.get("width") > 100;
                const isLargerHeight = model.get("height") > 100;

                let size = {
                    width:  60 / experienceSize.width  * 100,
                    height: 75 / experienceSize.height * 100
                };

                let position;

                if (model.get("src")) {
                    if (isLargerWidth || isLargerHeight) {
                        // make sure the size is not bigger than scene
                        size = new Size(100, 100);
                        size = size.shrinkToAspectRatio(model.get("width") / model.get("height"));
                    } else {
                        size = {
                            width: model.get("width"),
                            height: model.get("height")
                        };
                    }

                    position = {
                        left: model.get("left"),
                        top: model.get("top")
                    };
                } else {
                    position = {
                        left: model.get("left") - size.width / 2,
                        top: model.get("top") - size.height / 2
                    };
                }

                model.set({
                    width: size.width,
                    height: size.height,
                    left: Math.max(position.left, 0),
                    top: Math.max(position.top, 0)
                });
            }
        },
        LINE: {
            order: 110,
            propertiesViewClass: LineWidgetPropertiesView,
            tooltip: "Create a line.",
            freeTransformConfig: {
                rotate: false,
                rotateScale: true,
                scaleUniform: false,
                scaleX: false,
                scaleY: false
            },
            initializer: centerModel
        },
        LINK: {
            order: 30,
            propertiesViewClass: LinkWidgetPropertiesView,
            tooltip: "Create a link of any size on top of anything on the scene.",
            hideOnBackgroundScene: true,
            initializer: centerModel
        },
        NEXT_SCENE: {
            order: 80,
            tooltip: "Use the standard arrow or select an image, from your image library, for users to click on and move to the next scene.",
            hideOnBackgroundScene: true,
            initializer(model) {
                model.set("action", new SceneNavigationAction({target: "next"}));
            },
            suspend: suspendForEmailMode
        },
        PREVIOUS_SCENE: {
            order: 70,
            tooltip: "Use the standard arrow or select an image, from your image library, for users to click on and move to the previous scene.",
            hideOnBackgroundScene: true,
            initializer(model) {
                model.set("action", new SceneNavigationAction({target: "previous"}));
            },
            suspend: suspendForEmailMode
        },
        PRODUCT: {
            order: null, // not ordered, handle specifically because typically only a single product widget implementation at runtime
            propertiesViewClass: ProductWidgetPropertiesView,
            hideOnBackgroundScene: true,
            initializer: centerModel,
            suspend: suspendForEmailMode
        },
        SCENE_NAVIGATOR: {
            order: 90,
            propertiesViewClass: SceneNavigatorWidgetPropertiesView,
            icon: "scene-slider",
            tooltip: "Use this widget to navigate between scenes.",
            hideOnBackgroundScene: true,
            freeTransformConfig: {
                rotate: false,
                scaleUniform: false,
                scaleX: false,
                scaleY: false
            },
            initializer(model) {
                model.set("centeredPivotPoint", true);
            },
            suspend: suspendForEmailMode
        },
        SHAPE: {
            order: 100,
            propertiesViewClass: ShapeWidgetPropertiesView,
            icon: "box",
            tooltip: "Create a shape.",
            initializer: centerModel
        },
        SOCIAL_SHARING: {
            order: 60,
            propertiesViewClass: SocialSharingWidgetPropertiesView,
            icon: "social",
            tooltip: "Use this widget to share Experiences.",
            hideOnBackgroundScene: true,
            freeTransformConfig: {
                rotate: false,
                scaleUniform: false,
                scaleX: false,
                scaleY: false
            },
            initializer(model) {
                const lastUsed = Storage.Local.get(Storage.Keys.LAST_USED_SOCIAL_PROPERTIES);
                const picked = pick(
                    lastUsed,
                    "style",
                    "networks"
                );
                picked.centeredPivotPoint = true;
                model.set(picked);
            },
            suspend: suspendForEmailMode
        },
        TEXT: {
            order: 10, // first
            propertiesViewClass: TextWidgetPropertiesView,
            tooltip: "Type in your text and select a font, size, color and/or animation.",
            freeTransformConfig: {
                edgeTranslate: true,
                scaleUniform: false,
                scaleY: false
            },
            initializer(model) {
                // Apply last used text properties, if any...
                const lastUsed = Storage.Local.get(Storage.Keys.LAST_USED_TEXT_PROPERTIES);
                const picked = pick(
                    lastUsed,
                    "fontProvider",
                    "color",
                    "fontSize",
                    "fontStyle",
                    "fontMetadata",
                    "fontWeight",
                    "fontFamily"
                );
                model.set(picked);
            },
            enabled() {
                return LoggedInUserState.get("companySettings").get("textWidgetEnabled");
            }
        },
        TEXT_ENHANCED: {
            order: 11,
            propertiesViewClass: TextEnhancedWidgetPropertiesView,
            tooltip: "Type in your text and select a font, size, color and/or animation.",
            freeTransformConfig: {
                edgeTranslate: true,
                scaleUniform: false,
                scaleY: false
            },
            initializer() {},
            enabled() {
                return LoggedInUserState.get("companySettings").get("enhancedTextWidgetEnabled");
            }
        },
        YOUTUBE: {
            order: 50,
            propertiesViewClass: YoutubeWidgetPropertiesView,
            tooltip: "Insert a YouTube video.",
            initializer(model) {
                if (!model.get("videoID")) {
                    centerModel(model);
                }
            },
            suspend: suspendForEmailMode
        },
        BUTTON: {
            order: 65,
            propertiesViewClass: ButtonWidgetPropertiesView,
            expandPropertyView: "buttonExpanded",
            tooltip: "Create a button.",
            hideOnBackgroundScene: true,
            freeTransformConfig: {
                rotate: true,
                scaleUniform: false,
                rotateScale: false,
                scaleX: false,
                scaleY: false
            },
            initializer(model) {
                // Apply last used text properties, if any...
                const props = Storage.Local.get(Storage.Keys.LAST_USED_BUTTON_PROPERTIES);

                if (!props.icon) {
                    props.icon = MaterialIcons.modelFromId("play_circle_filled");
                }

                model.set(props, {silent: true});

                centerModel(model);
            }
        }
    }, (descriptor) => { // called once each constant has been mixed
        // For some odd reason, some icons are named inconsistently... :/
        if (!descriptor.icon) {
            descriptor.icon = descriptor.id;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/WidgetTypesEditorMixin.js